/**
 * Tolerant parser for the free-text LLM analysis assessment.
 *
 * The backend stores the assessment as plain text (`analyses.payload.analysis`)
 * with sections MANDATED by the analyzer prompt
 * (`pkg/service/analyze.go:51-91`): Summary / Findings / Severity Assessment
 * (exactly one of Low/Medium/High/Critical) / Recommendations. Severity is
 * NOT a stored column — it must be parsed out of the text. This module is the
 * single documented derivation; everything it returns is a pure function of
 * `AnalysisRecord.payload.analysis`.
 *
 * The parser is deliberately tolerant of LLM formatting drift: headers may be
 * decorated with leading `#`/`##`, surrounding `**bold**`, and a trailing `:`,
 * and are matched case-insensitively. Findings accept both
 * `- **Title**: detail` and `- Title: detail` bullets.
 */

import type { AnalysisRecord } from './analysis'

/**
 * Parsed severity, normalized to the app-wide lowercase `Severity` scale.
 * Backend: the mandated "Severity Assessment" section (prose "Low"/"Medium"/
 * "High"/"Critical" — matched case-insensitively, emitted lowercase).
 */
export type AnalysisSeverity = 'critical' | 'high' | 'medium' | 'low'

/** One parsed finding from the Findings section. */
export interface ParsedFinding {
  title: string
  detail: string
}

/** Structured projection of a free-text analysis assessment. */
export interface ParsedAnalysis {
  /** derived: first of Critical|High|Medium|Low in the Severity Assessment section. */
  severity: AnalysisSeverity
  /** derived: text under the Summary section. */
  summary: string
  /** derived: bullet list under the Findings section. */
  findings: ParsedFinding[]
  /** derived: bullet/line list under the Recommendations section. */
  recommendations: string[]
}

type SectionKey = 'summary' | 'findings' | 'severity' | 'recommendations'

/** Canonical section title text (lowercased) → key. */
const SECTION_TITLES: ReadonlyArray<readonly [SectionKey, string]> = [
  ['summary', 'summary'],
  ['findings', 'findings'],
  ['severity', 'severity assessment'],
  ['recommendations', 'recommendations'],
]

/**
 * Strip header decoration: leading `#`s, surrounding `**`, surrounding
 * whitespace, and a single trailing `:`. Returns the bare, lowercased title.
 */
function normalizeHeader(line: string): string {
  let s = line.trim()
  s = s.replace(/^#{1,6}\s*/, '') // markdown header hashes
  s = s.replace(/\*+/g, '') // bold/italic stars anywhere
  s = s.trim()
  s = s.replace(/:\s*$/, '') // trailing colon
  return s.trim().toLowerCase()
}

/** Match a line to a known section header, or undefined if it is body text. */
function sectionForLine(line: string): SectionKey | undefined {
  const norm = normalizeHeader(line)
  if (norm.length === 0) return undefined
  for (const [key, title] of SECTION_TITLES) {
    if (norm === title) return key
  }
  return undefined
}

const SEVERITY_ORDER: readonly AnalysisSeverity[] = [
  'critical',
  'high',
  'medium',
  'low',
]

/**
 * First severity token appearing in the given text. The prompt mandates
 * exactly one; if drift produces several we take the first occurrence by
 * position (not by rank) to match what a reader sees.
 */
function firstSeverity(text: string): AnalysisSeverity | undefined {
  const lower = text.toLowerCase()
  let best: { sev: AnalysisSeverity; at: number } | undefined
  for (const sev of SEVERITY_ORDER) {
    const at = lower.indexOf(sev)
    if (at !== -1 && (best === undefined || at < best.at)) {
      best = { sev, at }
    }
  }
  return best?.sev
}

/** Strip a leading bullet marker (`-`, `*`, `•`) from a line. */
function stripBullet(line: string): string {
  return line.replace(/^\s*[-*•]\s+/, '').trim()
}

/**
 * Parse one Findings bullet. Accepts `**Title**: detail` and `Title: detail`;
 * a colon separates title from detail. Bullets with no colon become a
 * title-only finding (empty detail).
 */
function parseFinding(raw: string): ParsedFinding | undefined {
  const body = stripBullet(raw)
  if (body.length === 0) return undefined
  const colon = body.indexOf(':')
  if (colon === -1) {
    return { title: body.replace(/\*+/g, '').trim(), detail: '' }
  }
  const title = body.slice(0, colon).replace(/\*+/g, '').trim()
  const detail = body.slice(colon + 1).trim()
  return { title, detail }
}

function isBullet(line: string): boolean {
  return /^\s*[-*•]\s+/.test(line)
}

/**
 * Parse a free-text analysis assessment into a structured view.
 *
 * Pure: depends only on `record.payload.analysis`. Missing sections yield
 * empty results; a missing/unparseable severity falls back to `'medium'`
 * (neutral default — never silently drops the assessment).
 */
export function parseAnalysis(record: AnalysisRecord): ParsedAnalysis {
  const text = record.payload.analysis ?? ''
  const lines = text.split(/\r?\n/)

  const buckets: Record<SectionKey, string[]> = {
    summary: [],
    findings: [],
    severity: [],
    recommendations: [],
  }

  let current: SectionKey | undefined
  for (const line of lines) {
    const header = sectionForLine(line)
    if (header) {
      current = header
      continue
    }
    if (current) buckets[current].push(line)
  }

  const summary = buckets.summary.join('\n').trim()

  const findings = buckets.findings
    .filter((l) => isBullet(l))
    .map(parseFinding)
    .filter((f): f is ParsedFinding => f !== undefined && f.title.length > 0)

  const recommendations = buckets.recommendations
    .map((l) => (isBullet(l) ? stripBullet(l) : l.trim()))
    .map((l) => l.replace(/\*+/g, '').trim())
    .filter((l) => l.length > 0)

  const severity = firstSeverity(buckets.severity.join('\n')) ?? 'medium'

  return { severity, summary, findings, recommendations }
}
